import { FeatureCollection, Point } from 'geojson'
import { RegisterTemplatePropertyType } from '@core/entities/register/RegisterTemplateProperty'
import { SiteBoxDocument } from '@core/entities/SiteBoxDocument/SiteBoxDocument'
import { DocumentIntelligenceStatus } from '@core/entities/SiteBoxDocument/types'

export type MediaGeoJSONProperties = {
  projectId?: number
  referencedResourceId?: number
  referencedResourceType?: string
  inputType?: string
  w3wLocation?: string
  locationAccuracyInMeters?: number
  locationAccuracyStatus?: string
  photoTakenOn?: string
  postCode?: string
  compass?: string
  isThumbnail?: boolean
  photoDirection?: string
  elevationInMeters?: number
}

export type MediaGeoJSON = FeatureCollection<Point, MediaGeoJSONProperties>

/**
 * Shared base for all attachment-like objects (proofs and documents).
 * Contains the fields common to both the UI presentation layer and the
 * API persistence layer.
 */
export type AttachmentBase = {
  id?: number
  downloadUrl?: string
  getUrl?: string
  siteBoxDocumentVersionId?: number
  name?: string
}

/**
 * Document Intelligence appraisal fields — only relevant for SiteBox
 * documents that have been through the DI pipeline.
 */
type DocumentAppraisalFields = {
  documentIntelligenceStatus?: DocumentIntelligenceStatus | string
  reviewedByName?: string
  reviewedAt?: string
}

/**
 * UI-layer attachment representing a renderable proof (image or PDF).
 * `type` is required for render decisions (img vs pdf).
 * `key` is optional because newly-captured photos don't have one yet.
 * `uri` supports local files not yet uploaded.
 *
 * Includes DocumentAppraisalFields for backward compatibility with older data
 * where flat fields were denormalized from the SiteBox document. New code
 * should read from the nested `siteBoxDocument` reference instead.
 */
export type Proof = AttachmentBase &
  DocumentAppraisalFields & {
    key?: string
    type: string
    uri?: string
    siteBoxDocument?: SiteBoxDocument
  }

/**
 * API/persistence-layer document as returned by the server.
 * `key` is required because every persisted document has an S3 key.
 */
export type SubmissionDocument = AttachmentBase &
  DocumentAppraisalFields & {
    key: string
    //Should be ISiteBoxDocument but getting a typescript type instantiation excessively deep error
    siteBoxDocument?: any
  }

export type SubmissionProofCoordinates = {
  latitude?: number
  longitude?: number
  accuracy?: number
  photoTakenOn?: string
}

export type SubmissionProof = {
  key: string
  downloadUrl?: string
  coordinates?: SubmissionProofCoordinates
  //Should be MediaGeoJSON but getting a typescript type instantiation excessively deep error
  geoJSON?: any
}

export const ALL_PROOF_TYPES = ['pdf', 'img'] as const
type ProofTypeTuple = typeof ALL_PROOF_TYPES
export type ProofType = ProofTypeTuple[number]

export const ALL_REQUIRED_OPTIONAL_TYPES = [
  'REQUIRED',
  'OPTIONAL',
  'NOT_REQUIRED',
] as const
type RequiredOptionalTypeTuple = typeof ALL_REQUIRED_OPTIONAL_TYPES
export type RequiredOptionalType = RequiredOptionalTypeTuple[number]

export type TemplateQuestionRegisterItemPropertyType = {
  id: string
  isLinked: boolean
  name: string
  required: boolean
  sortable: boolean
  type: RegisterTemplatePropertyType
}

export const AllowedMediaTypes = [
  'DOCUMENTS_ONLY',
  'PHOTOS_ONLY',
  'DOCUMENTS_AND_PHOTOS',
] as const

type AllowedMediaTypesTuple = typeof AllowedMediaTypes
export type AllowedMediaTypes = AllowedMediaTypesTuple[number]

export const DOCUMENTS_ONLY = AllowedMediaTypes[0]
export const PHOTOS_ONLY = AllowedMediaTypes[1]
export const DOCUMENTS_AND_PHOTOS = AllowedMediaTypes[2]
